import React, { ChangeEvent, useId } from 'react';
import { Document } from '@contentful/rich-text-types';
import { Block, BLOCKS, Inline, INLINES } from '@contentful/rich-text-types';

import { Flexbox } from '../../Flexbox';
import { Text } from '../../Text';
import RichText from '../../RichText';
import { EmbeddedInlineEntryNode } from '../../RichText/Nodes';
import { InlineAnchor } from '../../RichText/styles';

import { formFieldEnum } from '../../SplitModule/Form/shared';

import { CheckboxInput, RadioInput, StyledLabel } from './style';

interface CheckboxProps {
  type: formFieldEnum.CHECKBOX | formFieldEnum.RADIO;
  value: string | number | readonly string[] | undefined;
  checked: boolean;
  label: string;
  name?: string;
  error?: string;
  required?: boolean;
  disclaimer?: Document | null;
  handleOnChange?: (
    event: ChangeEvent<HTMLInputElement>,
    value: boolean
  ) => void;
  testId?: string;
}

export function Checkbox({
  type,
  name,
  value,
  checked,
  required,
  label,
  disclaimer,
  handleOnChange
}: CheckboxProps) {
  const checkboxId = useId();

  return (
    <Flexbox flexDirection="row" alignItems="flex-start" paddingBottom="ml">
      <Flexbox padding={{ 0: ['2xs', 's'] }}>
        {type === formFieldEnum.RADIO ? (
          <RadioInput
            type="radio"
            id={checkboxId}
            name={name}
            value={value}
            checked={checked}
            required={required}
            onChange={(e) => {
              if (handleOnChange) {
                handleOnChange(e, e.target.checked);
              }
            }}
            data-testid={`radio-input-${label}`}
          />
        ) : (
          <CheckboxInput
            type="checkbox"
            id={checkboxId}
            name={name}
            value={value}
            required={required}
            onChange={(e) => {
              if (handleOnChange) {
                handleOnChange(e, e.target.checked);
              }
            }}
            data-testid={`checkbox-input-${label}`}
          />
        )}
      </Flexbox>
      <Flexbox flexDirection="column" padding={{ desktop: [0, 's'] }}>
        <StyledLabel htmlFor={checkboxId}>{label}</StyledLabel>

        {disclaimer && (
          <RichText
            data={disclaimer}
            overrideOptions={{
              [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => (
                <Text
                  as="p"
                  fontWeight="p"
                  fontSize="s"
                  lineHeight="s"
                  colour="defaultText"
                  padding={{ 0: ['s', 0] }}
                  textAlign="left"
                >
                  {children}
                </Text>
              ),
              [INLINES.HYPERLINK]: (node, children) => (
                <InlineAnchor href={node.data.uri} colour="defaultText">
                  {children}
                </InlineAnchor>
              ),
              [INLINES.EMBEDDED_ENTRY]: (node, children) => (
                <EmbeddedInlineEntryNode
                  node={node}
                  colour="defaultText"
                  fontWeight="p"
                >
                  {children}
                </EmbeddedInlineEntryNode>
              )
            }}
          />
        )}
      </Flexbox>
    </Flexbox>
  );
}
